import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Platform, StyleSheet, View } from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { useDispatch } from 'react-redux';
import Typography from '../../components/Reusables/Typography';
import { AppAlert } from '../../services/AppAlert';
import { AppSnackbar } from '../../services/AppSnackbar';
import { useAppSelector } from '../../redux/helper';
import {
  SETUP_WIZARD_STEPS,
  clearSimplifiedProductDraft,
  completeOwnerSetupStep,
  completeOwnerSetupWizard,
  exitOwnerSetupReplay,
  goBackOwnerSetupStep,
  selectOwnerSetupEligibility,
  selectOwnerSetupWizard,
  setTestLocationOptedIn,
  skipOwnerSetupWizard,
  startOwnerSetupWizard,
  viewOwnerSetupStep,
  type SetupWizardStepId,
} from '../../redux/ownerActivationSlice';
import { selectBusinessInfo, selectEmployeeName } from '../../redux/AuthSlice';
import { updateAccountPreferences } from '../../api/backoffice';
import useBackofficeAxios from '../../hooks/useBackofficeAxios';
import useBackofficeOwnerAuth from '../../hooks/useBackofficeOwnerAuth';
import useLocation from '../../hooks/useLocation';
import { getApiMessage } from '../../utils/apiError';
import ownerActivationAnalytics from '../../services/ownerActivationAnalytics';
import {
  toOwnerSetupDurationBucket,
  type OwnerActivationAnalyticsContext,
} from '../../services/ownerActivationAnalyticsTypes';
import type { RootStackParamList } from '../../navigation/AppStack';
import {
  decideOwnerSetupBack,
  ownerSetupHomeResetAction,
} from '../../navigation/ownerSetupBackBehavior';
import SetupWizardShell from './components/SetupWizardShell';
import SetupProgressStep from './components/SetupProgressStep';
import WorkspaceOverviewStep from './components/WorkspaceOverviewStep';
import LocationEducationStep from './components/LocationEducationStep';
import SaleLoopStep from './components/SaleLoopStep';
import FirstProductStep from './components/FirstProductStep';
import PreferencesStep from './components/PreferencesStep';
import SetupCompleteStep from './components/SetupCompleteStep';
import { GTFonts, theme as staticTheme } from '../../theme';
import { fs, v } from '../../utils/libs';

/**
 * New-owner setup wizard route host (US1 T034, composed in US2 T046).
 *
 * Loading-safe: while session bootstrap resolves this renders a branded
 * loader, never Home. Steps 1–4 are the educational tour; Step 5 (first
 * product) and Step 6 (ready) receive their full implementations in US3/US4.
 *
 * Replay mode (US7) never mutates persisted wizard state: it drives a local
 * step cursor and exits back to the caller.
 */

type Props = NativeStackScreenProps<RootStackParamList, 'OwnerSetupWizard'>;

const analyticsPlatform = (): OwnerActivationAnalyticsContext['platform'] => {
  if (Platform.OS === 'ios') return 'ios';
  if (Platform.OS === 'android') return 'android';
  return 'web';
};

const OwnerSetupWizardScreen: React.FC<Props> = ({ navigation, route }) => {
  const dispatch = useDispatch();
  const eligibility = useAppSelector(selectOwnerSetupEligibility);
  const wizard = useAppSelector(selectOwnerSetupWizard);
  const businessInfo = useAppSelector(selectBusinessInfo);
  const employeeName = useAppSelector(selectEmployeeName);
  const currentLocation = useAppSelector(
    state => state.location.currentLocation,
  );
  const locationLoading = useAppSelector(state => state.location.isLoading);
  const axiosBackoffice = useBackofficeAxios();
  const { owner, autoLoginOwner } = useBackofficeOwnerAuth();
  const { fetchLocations } = useLocation();

  const isReplay = route.params?.mode === 'replay';
  const [replayStepId, setReplayStepId] = useState<SetupWizardStepId>(
    route.params?.initialStepId ?? 'setup_progress',
  );
  /** Opt-in for test location on the locations step; off by default. */
  const [testLocationEnabled, setTestLocationEnabled] = useState(false);
  const [savingTestLocationPref, setSavingTestLocationPref] = useState(false);

  const resolving =
    !isReplay &&
    eligibility.eligible &&
    eligibility.sessionBootstrap === 'pending';

  const stepId: SetupWizardStepId = isReplay
    ? replayStepId
    : (wizard.currentStepId ?? 'setup_progress');
  const stepIdRef = useRef(stepId);
  stepIdRef.current = stepId;
  const stepIndex = Math.max(0, SETUP_WIZARD_STEPS.indexOf(stepId));
  const totalSteps = SETUP_WIZARD_STEPS.length;

  const analyticsContext = useMemo(
    (): OwnerActivationAnalyticsContext => ({
      onboarding_version: 1,
      platform: analyticsPlatform(),
      entry_mode: isReplay ? 'replay' : wizard.entryMode,
      business_type_group: businessInfo?.type || undefined,
    }),
    [businessInfo?.type, isReplay, wizard.entryMode],
  );

  const stepProperties = useCallback(
    (id: SetupWizardStepId) => ({
      ...analyticsContext,
      step_id: id,
      step_index: SETUP_WIZARD_STEPS.indexOf(id),
    }),
    [analyticsContext],
  );

  // Wizard lifecycle: persist start + emit started/resumed/reopened once.
  const lifecycleTrackedRef = useRef(false);
  useEffect(() => {
    if (resolving) return;
    if (lifecycleTrackedRef.current) return;
    if (isReplay) {
      lifecycleTrackedRef.current = true;
      ownerActivationAnalytics.track('owner_setup_reopened', {
        ...analyticsContext,
        current_step_id: replayStepId,
      });
      return;
    }
    if (!eligibility.eligible) return;
    if (wizard.status === 'not_started' || wizard.status === 'in_progress') {
      const entryMode = wizard.status === 'not_started' ? 'initial' : 'resume';
      dispatch(
        startOwnerSetupWizard({
          startedAt: new Date().toISOString(),
          entryMode,
        }),
      );
      lifecycleTrackedRef.current = true;
      ownerActivationAnalytics.track(
        entryMode === 'initial' ? 'owner_setup_started' : 'owner_setup_resumed',
        {
          ...analyticsContext,
          entry_mode: entryMode,
          current_step_id: wizard.currentStepId ?? 'setup_progress',
        },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, eligibility.eligible, isReplay, resolving, wizard.status]);

  // Persist + announce each viewed step.
  const lastViewedStepRef = useRef<SetupWizardStepId | null>(null);
  useEffect(() => {
    if (resolving) return;
    if (lastViewedStepRef.current === stepId) return;
    lastViewedStepRef.current = stepId;
    dispatch(
      viewOwnerSetupStep({ stepId, viewedAt: new Date().toISOString() }),
    );
    ownerActivationAnalytics.track(
      'owner_setup_step_viewed',
      stepProperties(stepId),
    );
  }, [dispatch, resolving, stepId, stepProperties]);

  const goToHome = useCallback(() => {
    navigation.reset(ownerSetupHomeResetAction());
  }, [navigation]);

  const advanceWizardStep = useCallback(() => {
    if (isReplay) {
      const next = SETUP_WIZARD_STEPS[stepIndex + 1];
      if (next) setReplayStepId(next);
      return;
    }
    ownerActivationAnalytics.track(
      'owner_setup_step_completed',
      stepProperties(stepId),
    );
    dispatch(completeOwnerSetupStep({ stepId }));
  }, [dispatch, isReplay, stepId, stepIndex, stepProperties]);

  const handleContinue = useCallback(() => {
    advanceWizardStep();
  }, [advanceWizardStep]);

  const handleLocationsContinue = useCallback(async () => {
    if (isReplay) {
      advanceWizardStep();
      return;
    }
    if (savingTestLocationPref) return;
    dispatch(setTestLocationOptedIn({ optedIn: testLocationEnabled }));

    setSavingTestLocationPref(true);
    try {
      let accessToken = owner?.accessToken || '';
      if (!accessToken) {
        const login = await autoLoginOwner();
        accessToken = String(login?.accessToken || '');
      }
      if (!accessToken) {
        AppSnackbar.show(
          'Could not save test location preference. You can change this later in Settings.',
          'error',
        );
      } else {
        const res = await updateAccountPreferences(
          axiosBackoffice as any,
          { test_location_enabled: testLocationEnabled },
          { accessToken },
        );
        if (res?.status === 200 || res?.data?.status === 200) {
          await fetchLocations();
        } else {
          AppSnackbar.show(
            'Could not save test location preference',
            'error',
          );
        }
      }
    } catch (e: any) {
      AppSnackbar.show(
        getApiMessage(e, 'Could not save test location preference'),
        'error',
      );
    } finally {
      setSavingTestLocationPref(false);
    }

    // User may have left the step (Back/Skip) while the request was in flight.
    if (stepIdRef.current === 'locations') {
      advanceWizardStep();
    }
  }, [
    advanceWizardStep,
    autoLoginOwner,
    axiosBackoffice,
    fetchLocations,
    isReplay,
    owner?.accessToken,
    savingTestLocationPref,
    testLocationEnabled,
  ]);

  const handleBack = useCallback(() => {
    if (savingTestLocationPref) return;
    const decision = decideOwnerSetupBack({
      isReplay,
      stepIndex,
      steps: SETUP_WIZARD_STEPS,
    });
    switch (decision.kind) {
      case 'replay_step_back':
        setReplayStepId(decision.toStepId);
        return;
      case 'replay_exit':
        dispatch(exitOwnerSetupReplay());
        navigation.goBack();
        return;
      case 'step_back':
        dispatch(goBackOwnerSetupStep());
        return;
      case 'noop':
      default:
        return;
    }
  }, [dispatch, isReplay, navigation, savingTestLocationPref, stepIndex]);

  const handleSkipSetup = useCallback(async () => {
    if (savingTestLocationPref) return;
    if (isReplay || stepId === 'setup_progress') return;
    const confirmed = await AppAlert.confirm(
      'Skip the quick setup?',
      'You can continue from Getting Started on Home or reopen this guide later from Settings.',
      { okText: 'Skip setup', cancelText: 'Continue setup' },
    );
    if (!confirmed) {
      ownerActivationAnalytics.track(
        'owner_setup_skip_cancelled',
        stepProperties(stepId),
      );
      return;
    }
    ownerActivationAnalytics.track('owner_setup_skipped', {
      ...stepProperties(stepId),
      skip_from_step_id: stepId as Exclude<SetupWizardStepId, 'setup_progress'>,
    });
    dispatch(
      skipOwnerSetupWizard({
        stepId: stepId as Exclude<SetupWizardStepId, 'setup_progress'>,
        skippedAt: new Date().toISOString(),
      }),
    );
    goToHome();
  }, [
    dispatch,
    goToHome,
    isReplay,
    savingTestLocationPref,
    stepId,
    stepProperties,
  ]);

  const handleGoHome = useCallback(() => {
    if (isReplay) {
      dispatch(exitOwnerSetupReplay());
      navigation.goBack();
      return;
    }
    const startedAtMs = wizard.startedAt ? Date.parse(wizard.startedAt) : NaN;
    ownerActivationAnalytics.track('owner_setup_completed', {
      ...analyticsContext,
      product_created_during_wizard: wizard.productCreatedDuringWizard,
      duration_bucket: toOwnerSetupDurationBucket(
        Number.isFinite(startedAtMs) ? Date.now() - startedAtMs : Number.NaN,
      ),
    });
    dispatch(
      completeOwnerSetupWizard({ completedAt: new Date().toISOString() }),
    );
    goToHome();
  }, [
    analyticsContext,
    dispatch,
    goToHome,
    isReplay,
    navigation,
    wizard.productCreatedDuringWizard,
    wizard.startedAt,
  ]);

  const handleSkipProductForNow = useCallback(() => {
    if (isReplay) {
      setReplayStepId('preferences');
      return;
    }
    ownerActivationAnalytics.track(
      'owner_setup_product_skipped',
      stepProperties('first_product'),
    );
    dispatch(clearSimplifiedProductDraft());
    dispatch(completeOwnerSetupStep({ stepId: 'first_product' }));
  }, [dispatch, isReplay, stepProperties]);

  const handleSkipPreferencesForNow = useCallback(() => {
    if (isReplay) {
      setReplayStepId('locations');
      return;
    }
    ownerActivationAnalytics.track(
      'owner_setup_step_completed',
      stepProperties('preferences'),
    );
    dispatch(completeOwnerSetupStep({ stepId: 'preferences' }));
  }, [dispatch, isReplay, stepProperties]);

  if (resolving) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={staticTheme.colors.primary} />
        <Typography style={styles.loadingText}>
          Preparing your setup guide…
        </Typography>
      </View>
    );
  }

  const showBack = stepIndex > 0 || isReplay;
  const showSkip = !isReplay && stepId !== 'setup_progress';

  switch (stepId) {
    case 'setup_progress':
      return (
        <SetupWizardShell
          stepIndex={stepIndex}
          totalSteps={totalSteps}
          eyebrow="Quick setup"
          heading="You're almost ready to use BizPlus POS"
          supportingCopy="Your account and business information are set up. Take a quick tour, then add your first product."
          onBack={showBack ? handleBack : undefined}
          primaryLabel="Continue setup"
          onPrimaryPress={handleContinue}
          testID="wizard-step-setup-progress">
          <SetupProgressStep
            businessName={businessInfo?.name}
            ownerName={employeeName}
          />
        </SetupWizardShell>
      );
    case 'workspace':
      return (
        <SetupWizardShell
          stepIndex={stepIndex}
          totalSteps={totalSteps}
          eyebrow="Quick setup"
          heading="Get to know your workspace"
          supportingCopy="Three places do most of the work in BizPlus POS."
          onBack={handleBack}
          onSkip={showSkip ? handleSkipSetup : undefined}
          primaryLabel="Continue"
          onPrimaryPress={handleContinue}
          testID="wizard-step-workspace">
          <WorkspaceOverviewStep />
        </SetupWizardShell>
      );
    case 'sale_loop':
      return (
        <SetupWizardShell
          stepIndex={stepIndex}
          totalSteps={totalSteps}
          eyebrow="Quick setup"
          heading="How a sale works"
          supportingCopy="Every sale follows the same simple loop."
          onBack={handleBack}
          onSkip={showSkip ? handleSkipSetup : undefined}
          primaryLabel="Continue"
          onPrimaryPress={handleContinue}
          testID="wizard-step-sale-loop">
          <SaleLoopStep businessType={businessInfo?.type} />
        </SetupWizardShell>
      );
    case 'first_product':
      return (
        <FirstProductStep
          stepIndex={stepIndex}
          totalSteps={totalSteps}
          onBack={handleBack}
          onSkipSetup={showSkip ? handleSkipSetup : undefined}
          onSkipProduct={handleSkipProductForNow}
          onProductSatisfied={handleContinue}
          businessType={businessInfo?.type}
        />
      );
    case 'preferences':
      return (
        <SetupWizardShell
          stepIndex={stepIndex}
          totalSteps={totalSteps}
          eyebrow="Quick setup"
          heading="Set up your preferences"
          supportingCopy="You can change any of these later in Settings."
          onBack={handleBack}
          onSkip={showSkip ? handleSkipSetup : undefined}
          primaryLabel="Continue"
          onPrimaryPress={handleContinue}
          secondaryLabel="Skip for now"
          onSecondaryPress={handleSkipPreferencesForNow}
          secondaryAccessibilityHint="Keep the defaults and continue"
          testID="wizard-step-preferences">
          <PreferencesStep />
        </SetupWizardShell>
      );
    case 'locations':
      return (
        <SetupWizardShell
          stepIndex={stepIndex}
          totalSteps={totalSteps}
          eyebrow="Quick setup"
          heading="Your location controls your data"
          onBack={handleBack}
          onSkip={showSkip ? handleSkipSetup : undefined}
          primaryLabel="Continue"
          onPrimaryPress={handleLocationsContinue}
          primaryBusy={savingTestLocationPref}
          testID="wizard-step-locations">
          <LocationEducationStep
            currentLocationName={String(
              currentLocation?.bp_location_name || '',
            )}
            locationLoading={locationLoading}
            testLocationEnabled={testLocationEnabled}
            onTestLocationEnabledChange={setTestLocationEnabled}
            testLocationPreferenceBusy={savingTestLocationPref}
          />
        </SetupWizardShell>
      );
    case 'ready_for_home':
    default:
      return (
        <SetupWizardShell
          stepIndex={stepIndex}
          totalSteps={totalSteps}
          eyebrow="Quick setup"
          heading="You're ready to use BizPlus POS"
          supportingCopy={
            wizard.productCreatedDuringWizard
            ? 'Thats it! '
              : 'Thats it!'
          }
          onBack={handleBack}
          primaryLabel="Go to Home"
          onPrimaryPress={handleGoHome}
          testID="wizard-step-ready">
          <SetupCompleteStep
            productCreated={wizard.productCreatedDuringWizard}
          />
        </SetupWizardShell>
      );
  }
};

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: staticTheme.colors.surface,
  },
  loadingText: {
    marginTop: v(12),
    fontSize: fs(14),
    lineHeight: fs(20),
    fontFamily: GTFonts.regular,
    color: staticTheme.colors.text,
  },
});

export default OwnerSetupWizardScreen;
